<script setup lang="ts">
import { isOpen, useJobTray } from "@/composables/useJobTray"
import { useMenuBuilder } from "@/composables/useMenuBuilder"
import type { JobTicketDto } from "@/model/worker/job-ticket-dto"
import type { JobTicketArtifactDto } from "@/model/worker/job-ticket-artifact-dto"
import { useApp, useAppConfig } from "@/vf"
import { computed, watch } from "vue"

/**
 * Follows the background runs the current user triggered — of every job kind.
 *
 * Lives in the app shell rather than on the page the run was started from: a run outlives the
 * page, and the user should still get their results after navigating away. The open runs are
 * read from the server, so a reload — or a different tab — picks them up again without anything
 * being remembered in the browser.
 */
const { apiLink } = useApp()
const { appConfig } = useAppConfig()
const { tickets, dismiss, cancel, start, stop } = useJobTray()
const { myOfficeRoute, myStaffRoute } = useMenuBuilder()

/**
 * Only someone who can start a run can ever have one, and that is My Office and staff members —
 * everyone else would poll forever for a list that stays empty.
 */
const canHaveJobs = computed(() => !!appConfig.value?.account && (!!myOfficeRoute.value || !!myStaffRoute.value))

watch(canHaveJobs, canHave => (canHave ? start() : stop()), { immediate: true })

function artifactUrl(ticket: JobTicketDto, artifact: JobTicketArtifactDto): string {
    return apiLink(`/worker/job/${ticket.id}/artifact/${artifact.id}`)
}

/**
 * A run whose progress says nothing yet: queued, or running but no item has announced a total
 * so far. The bar runs full width in muted grey then — a real percentage would claim a number
 * nobody knows.
 */
function isIndeterminate(ticket: JobTicketDto): boolean {
    return ticket.status === "Pending" || ticket.progressTotal === 0
}

function percent(ticket: JobTicketDto): number {
    if (ticket.progressTotal === 0) {
        return 0
    }

    return Math.min(Math.round((ticket.progressDone / ticket.progressTotal) * 100), 100)
}

/**
 * Counts failed work items, not missing records — a batch books itself as failed once, however
 * many of its records failed. Reads as a sentence for one as well as for many.
 */
function missingNote(ticket: JobTicketDto): string {
    return ticket.failedItems === 1
        ? "1 Batch fehlgeschlagen — Grund steht im Archiv"
        : `${ticket.failedItems} Batches fehlgeschlagen — Gründe stehen im Archiv`
}

function label(ticket: JobTicketDto): string {
    if (isOpen(ticket) && ticket.isCancelRequested) {
        // The handlers stop at their next checkpoint, so this is what the tile shows until the
        // run really ends — the progress bar keeps moving a little longer
        return "Wird abgebrochen …"
    }
    if (ticket.status === "Failed") {
        return "Fehlgeschlagen"
    }
    if (ticket.status === "Cancelled") {
        return "Abgebrochen"
    }
    if (ticket.status === "Succeeded") {
        return "Fertig"
    }
    if (ticket.status === "Pending") {
        return "Wartet auf einen freien Worker …"
    }
    if (ticket.progressTotal === 0) {
        return "Wird vorbereitet …"
    }
    if (ticket.progressDone >= ticket.progressTotal) {
        return "Wird abgeschlossen …"
    }

    return `${ticket.progressDone} von ${ticket.progressTotal}`
}
</script>

<template>
    <div v-if="canHaveJobs && tickets.length" class="job-tray">
        <div v-for="ticket of tickets" :key="ticket.id" class="card job-tray__item mb-2">
            <div class="card-body p-3">
                <div class="d-flex align-items-start">
                    <div class="mr-auto pr-2">
                        <strong>{{ ticket.title }}</strong>
                    </div>
                    <button type="button" class="job-tray__close" title="Ausblenden" @click="dismiss(ticket.id)">
                        <i class="fas fa-xmark"></i>
                    </button>
                </div>

                <div class="job-tray__label text-muted mt-2">{{ label(ticket) }}</div>

                <!-- Only a request; the tile keeps its progress bar until the run really ends -->
                <button
                    v-if="ticket.isCancellable && !ticket.isCancelRequested"
                    type="button"
                    class="job-tray__cancel"
                    @click="cancel(ticket.id)"
                >
                    Abbrechen
                </button>

                <div v-if="isOpen(ticket)" class="progress mt-2">
                    <div
                        class="progress-bar progress-bar-striped progress-bar-animated"
                        :class="isIndeterminate(ticket) ? 'bg-secondary' : 'bg-primary'"
                        :style="{ width: isIndeterminate(ticket) ? '100%' : `${percent(ticket)}%` }"
                        role="progressbar"
                        :aria-valuenow="isIndeterminate(ticket) ? 0 : percent(ticket)"
                        aria-valuemin="0"
                        aria-valuemax="100"
                    ></div>
                </div>

                <!-- Downloading does not dismiss: a run can carry several artifacts, the tile
                     stays until the user closes it -->
                <a
                    v-for="artifact of ticket.artifacts"
                    :key="artifact.id"
                    class="btn btn-primary btn-sm job-tray__download mt-2"
                    :href="artifactUrl(ticket, artifact)"
                >
                    <i class="fas fa-download mr-1"></i>
                    {{ ticket.artifacts.length === 1 ? "Herunterladen" : artifact.name }}
                </a>

                <div v-if="ticket.failedItems > 0 && ticket.artifacts.length" class="job-tray__note text-muted mt-1">
                    {{ missingNote(ticket) }}
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped lang="scss">
.job-tray {
    position: fixed;
    right: 1rem;
    bottom: 1rem;
    z-index: 1030;
    width: 20rem;
    max-width: calc(100vw - 2rem);
}

.job-tray__item {
    border: 1px solid #ced4da;
    border-left: 4px solid #007bff;
    border-radius: 0.5rem;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.15);
}

.job-tray__label,
.job-tray__note {
    font-size: 0.9rem;
}

.job-tray__cancel {
    border: none;
    background: transparent;
    padding: 0;
    margin-top: 0.25rem;
    font-size: 0.9rem;
    color: #6c757d;
    text-decoration: underline;
    cursor: pointer;

    &:hover {
        color: #dc3545;
    }
}

.job-tray__close {
    border: none;
    background: transparent;
    color: #adb5bd;
    line-height: 1;
    padding: 0;
    margin: 0;
    cursor: pointer;

    &:hover {
        color: #495057;
    }
}

.job-tray__download {
    // The project's buttons carry side margins for their angled shape; inside the narrow tray
    // that would push the button out of the card
    margin: 0;
    margin-right: 0.25rem;
}

.progress {
    height: 0.5rem;
    border-radius: 1rem;
    background-color: #e9ecef;
}

.progress-bar {
    border-radius: 1rem;
    transition: width 0.4s ease;
}
</style>
